import React, { forwardRef, useImperativeHandle, useState } from 'react'
import Dropzone, { type FileRejection } from 'react-dropzone'
import UploadIcon from '@mui/icons-material/Upload'
import Compressor from 'compressorjs'
import { supabase } from '../../supabaseClient'
import { Box, Grid } from '@mui/material'
import { ErrorMessageForm, PrimaryText } from '../ui/Text'
import { GREEN } from '../ui/Colors'

interface OwnProps {
    fileName: string
    originalImage?: string | undefined
    autoSave?: boolean
}

function AvatarPlaceholder({ oldImage, newImage }: any) {
    const imageUrl = newImage || oldImage
    if (imageUrl) {
        return <img src={imageUrl} alt="avatar" />
    }
    return (
        <Box flexDirection="column" justifyContent="center" alignItems="center">
            <UploadIcon fontSize="large" htmlColor={'#3A4D87'} />
        </Box>
    )
}
const PhotoUploader = forwardRef(
    ({ fileName, originalImage, autoSave = true }: OwnProps, ref) => {
        const [selectedFile, setSelectedFile] = useState<any>(null)
        useImperativeHandle(ref, () => ({ uploadImage }))
        const onDrop = async (
            acceptedFiles: any,
            rejectedFiles: FileRejection[]
        ) => {
            if (rejectedFiles.length > 0) return // handle error for unsupported files
            const file = acceptedFiles[0]
            setSelectedFile(file)
            await uploadImage(file)
        }
        const uploadImage = async (activeFile = selectedFile) => {
            if (activeFile) {
                // eslint-disable-next-line no-new
                new Compressor(activeFile, {
                    quality: 0.6,
                    async success(result: File) {
                        await supabase.storage
                            .from(
                                process.env.REACT_APP_SUPABASE_BUCKET_PICTURES_DATA
                            )
                            .update(`${fileName}`, result, { upsert: true })
                    },
                })
            }
        }
        return (
            <Box position="relative">
                <Dropzone
                    accept={{
                        'image/jpeg': ['.jpg', 'jpeg'],
                        'image/png': ['.png'],
                    }}
                    onDrop={onDrop}
                    multiple={false}
                >
                    {({ getRootProps, getInputProps, open }) => (
                        <Box
                            {...getRootProps()}
                            sx={{
                                backgroundColor: 'orange',
                                width: '4.5em',
                                height: '4.5em',
                                cursor: 'pointer',
                                borderRadius: '2.5em',
                            }}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <input {...getInputProps()} />
                            <AvatarPlaceholder
                                oldImage={originalImage}
                                newImage={null}
                            />
                        </Box>
                    )}
                </Dropzone>
            </Box>
        )
    }
)


const uploadImageToSupabase = async (
    file: File,
    fileName: string
): Promise<boolean> => {
    const { error } = await supabase
        .storage
        .from(process.env.REACT_APP_SUPABASE_BUCKET_FAQ_PICTURES)
        .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false // Empêche d'écraser un fichier existant
        })

    if (error) {
        console.error(`Erreur lors de l'upload de ${fileName}:`, error.message)
        return false
    }
    return true
}

export const ImageUploader = ({ getUploadName }: { getUploadName: (fileName: string) => void }) => {
    const [uploadSuccess, setUploadSuccess] = useState<boolean | null>(null)

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        const upload = await uploadImageToSupabase(file, file.name)
        if (upload) {
            setUploadSuccess(upload)
            getUploadName(file.name)
        } else {
            setUploadSuccess(upload)
        }
    }

    return (
        <Grid>
            <input type="file" accept="image/*" onChange={handleUpload} />
            {uploadSuccess === true && <PrimaryText color={GREEN} >Image uploadée avec succès !</PrimaryText >}
            {uploadSuccess === false && <ErrorMessageForm message={'Échec de l’upload.'}></ErrorMessageForm>}
        </Grid>
    )
}